/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { graphql } from 'react-apollo';

import { useMutation } from '@apollo/client';
import { compose } from 'recompose';

import { FormattedHTMLMessage } from 'js/lib/coursera.react-intl';
import { useParams } from 'js/lib/useRouter';

import { Button, InlineNotification, TextField, Typography2 } from '@coursera/cds-core';
import type { ButtonProps } from '@coursera/cds-core';
import { AddIcon, ArrowNextIcon, TrashIcon } from '@coursera/cds-icons';
import { email as isInvalidEmail } from '@coursera/coursera-ui/lib/utils/validation';
import { useTracker } from '@coursera/event-pulse/react';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import type { MouseDownProps } from 'bundles/enterprise-admin-grow-with-google/components/InviteLearnersScreen';
import { InviteLearnersByAllowlistModal } from 'bundles/enterprise-admin-program-creation/components/InviteLearnersByAllowlistModal';
import { ModalStates } from 'bundles/enterprise-admin-program-creation/constants';
import MultiDelete from 'bundles/enterprise-admin-program-creation/queries/ProgramInvitationsDomainAllowListMultiDeleteMutation.graphql';
import MultiUpdate from 'bundles/enterprise-admin-program-creation/queries/ProgramInvitationsDomainAllowListMultiUpdateMutation.graphql';
import type {
  MultiDeleteMutation as MultiDeleteMutationType,
  MultiDeleteMutationVariables,
} from 'bundles/enterprise-admin-program-creation/queries/__generated__/ProgramInvitationsDomainAllowListMultiDeleteMutation';
import type {
  MultiUpdateMutation as MultiUpdateMutationType,
  MultiUpdateMutationVariables,
} from 'bundles/enterprise-admin-program-creation/queries/__generated__/ProgramInvitationsDomainAllowListMultiUpdateMutation';
import type {
  EnterpriseUserProgramAllowListElement as AllowListElement,
  EnterpriseUserProgramAllowListQueryResults,
  EnterpriseUserProgramAllowListQueryVariables,
} from 'bundles/enterprise-admin-program-users/utils/EnterpriseUserProgramAllowListQuery';
import { enterpriseUserProgramAllowListQuery } from 'bundles/enterprise-admin-program-users/utils/EnterpriseUserProgramAllowListQuery';
import filterExistsOrDefault from 'bundles/enterprise-legacy-learner-home/utils/filterExistsOrDefault';
import type { InjectedNaptime } from 'bundles/naptimejs';
import Naptime from 'bundles/naptimejs';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/enterprise-admin-program-creation';

const TrackedButton = withSingleTracked({ type: 'BUTTON' })<ButtonProps>(Button);

const description = () =>
  css({
    marginTop: 'var(--cds-spacing-150)',
    marginBottom: 'var(--cds-spacing-400)',
    '&&': {
      fontSize: '20px',
      lineHeight: '28px',
    },
  });

const description2 = () =>
  css({
    marginTop: 'var(--cds-spacing-150)',
    '&&': {
      fontSize: '20px',
      lineHeight: '28px',
    },
  });

const errorStyles = css({
  padding: '4px 12px',
  background: '#FDF5F5',
  borderRadius: '4px',
});

const domainInputContainer = css({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
  gap: '12px',
});

const addDomainButton = () =>
  css({
    maxWidth: 'none',
    '&&': {
      padding: 'var(--cds-spacing-150) var(--cds-spacing-200)',
    },
  });

const allowlistContainer = () =>
  css({
    borderTop: `1px solid var(--cds-color-grey-300)`,
    borderBottom: `1px solid var(--cds-color-grey-300)`,
    listStyle: 'none',
    padding: '0',
    marginBottom: 'var(--cds-spacing-600)',
    maxHeight: '206px',
    overflowY: 'scroll',
    overflowX: 'hidden',
  });

const allowlistItem = () =>
  css({
    display: 'flex',
    justifyContent: 'space-between',
    margin: 'var(--cds-spacing-300) 0',
    width: '97%',
  });

const disabledButtonStyle = css({
  '&&': {
    color: '#e5e7e8',
    backgroundColor: '#c3c5c6',
    boxShadow: 'none',
  },
});

type PropsFromCaller = {
  programId: string;
  rosterId?: string;
  hasCompletedSurvey: boolean;
  onCancel?: () => void;
  onContinue?: () => void;
  cancelLabel?: string;
  onMouseDownGwG?: (value: MouseDownProps) => void;
  onReturnToAdminHome?: () => void;
};

type PropsFromEnterpriseUserProgramAllowListQuery = {
  allowlist: Array<AllowListElement>;
  refetchAllowlist?: () => Promise<unknown> | unknown;
};

type PropsFromNaptime = {
  naptime: InjectedNaptime;
};

export type Props = PropsFromCaller & PropsFromEnterpriseUserProgramAllowListQuery & PropsFromNaptime;

export const InviteLearnersByAllowlistInputs = ({
  onCancel,
  programId,
  rosterId,
  allowlist,
  onContinue,
  refetchAllowlist,
  hasCompletedSurvey,
  cancelLabel,
  onMouseDownGwG,
  onReturnToAdminHome,
}: Props) => {
  const allowlistContainerRef = React.useRef<HTMLUListElement>(null);
  const [domainInputText, setDomainInputText] = React.useState<string>('');
  const [modalState, setModalState] = React.useState<ModalStates>();
  const [customError, setCustomError] = React.useState<string | undefined>();
  const [selectedDomainToDelete, setSelectedDomainToDelete] = React.useState<string>('');
  const [hasSuccessfullyAddedDomain, setHasSuccessfullyAddedDomain] = React.useState<boolean>(false);
  const [hasDomainInputError, setHasDomainInputError] = React.useState<boolean>(false);

  const track = useTracker();
  const { thirdPartySlug, programSlug } = useParams();

  const [multiUpdateDomains] = useMutation<MultiUpdateMutationType, MultiUpdateMutationVariables>(MultiUpdate, {
    context: { clientName: 'gatewayGql' },
  });
  const [multiDeleteDomains] = useMutation<MultiDeleteMutationType, MultiDeleteMutationVariables>(MultiDelete, {
    context: { clientName: 'gatewayGql' },
  });

  const shouldContinue = hasCompletedSurvey && allowlist.length > 0;

  const onDomainInputTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputVal = e.target.value;
    setDomainInputText(inputVal);
    setHasDomainInputError(false);
  };

  const onModalClose = () => {
    setModalState(undefined);
    setCustomError(undefined);
  };

  const hasInvalidDomains = (domainsToAdd: Array<string>) =>
    domainsToAdd.some((domain) => isInvalidEmail(`prefix@${domain}`));

  const hasDuplicateDomains = (domainsToAdd: Array<string>) =>
    domainsToAdd.some(
      (domain, index) => allowlist.some((_) => _.emailDomain === domain) || domainsToAdd.indexOf(domain) !== index
    );

  const onAddDomains = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      const domainsToAdd = domainInputText.split(',').map((domainToAdd) => domainToAdd.trim().toLowerCase());
      if (hasInvalidDomains(domainsToAdd)) {
        setHasDomainInputError(true);
        return;
      }
      if (hasDuplicateDomains(domainsToAdd)) {
        setModalState(ModalStates.DuplicateDomainNames);
        return;
      }

      if (!rosterId) {
        return;
      }

      const response = await multiUpdateDomains({
        variables: {
          input: {
            programId: programId,
            emailDomains: domainsToAdd,
            rosterId: rosterId!,
            insertOnly: false,
          },
        },
      });

      if (response.errors) {
        const errorMessages = response.errors.map((error) => error.message).join(', ');
        setCustomError(errorMessages);
        setModalState(ModalStates.AddError);
      } else {
        if (refetchAllowlist) await refetchAllowlist();
        allowlistContainerRef?.current?.scrollTo(0, 0);
        setDomainInputText('');
        setHasSuccessfullyAddedDomain(true);
      }
    } catch {
      setModalState(ModalStates.AddError);
    }
  };

  const onDeleteDomainClick = (domainToDelete: string) => {
    setSelectedDomainToDelete(domainToDelete);
    setModalState(ModalStates.DeleteConfirm);
  };

  const onDeleteDomainConfirmClick = async () => {
    try {
      const response = await multiDeleteDomains({
        variables: {
          input: {
            programId,
            emailDomains: [selectedDomainToDelete],
          },
        },
      });
      if (response.errors) {
        const errorMessages = response.errors.map((error) => error.message).join(', ');
        setCustomError(errorMessages);
        setModalState(ModalStates.DeleteError);
      } else {
        if (refetchAllowlist) await refetchAllowlist();
        setModalState(ModalStates.DeleteSuccess);
        setSelectedDomainToDelete('');
      }
    } catch {
      setModalState(ModalStates.DeleteError);
    }
  };

  const onContinueClick = () => {
    if (onMouseDownGwG && !shouldContinue) {
      onMouseDownGwG({
        surveyError: !hasCompletedSurvey,
        domainError: !hasSuccessfullyAddedDomain,
      });
      return;
    }
    if (shouldContinue && onContinue) {
      onContinue();
    }
  };

  return (
    <TrackedDiv
      trackingName="invite_learners_allowlist"
      withVisibilityTracking={true}
      requireFullyVisible={false}
      trackClicks={false}
      atMostOnce={true}
      data-testid="invite-learners-allowlist"
    >
      <Typography2 variant="subtitleLarge" component="h2">
        {_t('Add your domain name(s)')}
      </Typography2>
      <Typography2 component="p" css={description()} variant="bodyPrimary" color="supportText">
        {_t(
          'Allow any email address associated with your domain name to join your learning program. You can even add multiple domain names. Any learner that creates an account or logs in using their email address with the same domain name will have access.'
        )}
      </Typography2>
      {hasDomainInputError ? (
        <Typography2 component="p" css={[description(), errorStyles]} variant="bodyPrimary" color="error">
          {_t('Enter a valid domain name')}
        </Typography2>
      ) : (
        <Typography2 component="p" css={description2()} variant="bodyPrimary">
          {_t('Add your domain name(s). Have more than one? Separate them by a comma.')}
        </Typography2>
      )}
      <form onSubmit={onAddDomains} css={domainInputContainer}>
        <TextField
          placeholder="domain.com, example.org"
          value={domainInputText}
          onChange={onDomainInputTextChange}
          validationStatus={hasDomainInputError ? 'error' : undefined}
          fullWidth
          aria-label={_t('Domains')}
          data-e2e="allowlist-domains-input-input"
        />
        <div style={{ width: '20%', minWidth: '168px' }}>
          <Button
            fullWidth
            css={addDomainButton()}
            variant="secondary"
            icon={<AddIcon color="interactive" />}
            iconPosition="before"
            type="submit"
            data-e2e="add-domains-button"
            data-testid="add-domains-button"
            onClick={() => {
              track('click_button', {
                button: { name: 'enterprise_add_domain_allowlist' },
                enterprise: {
                  ...(thirdPartySlug ? { organizationSlug: thirdPartySlug } : {}),
                  ...(programSlug ? { programSlug } : {}),
                },
              });
            }}
          >
            {_t('Add domain(s)')}
          </Button>
        </div>
      </form>
      <InlineNotification severity="warning" css={{ margin: 'var(--cds-spacing-300) 0 var(--cds-spacing-600)' }}>
        <Typography2 component="p">
          <FormattedHTMLMessage
            message={_t(
              'All subdomains associated with your domain will automatically be included (ex: test@{subdomain}.coursera.org). Please avoid adding free domain names as this may result in licenses being occupied by members outside your organization. '
            )}
            subdomain={<strong>{_t('subdomain')}</strong>}
          />
        </Typography2>
      </InlineNotification>
      {allowlist && allowlist.length > 0 && (
        <div data-testid="allowlist-items">
          <Typography2 component="p" css={description()} variant="bodyPrimary">
            {_t('List of domain name(s)')}
          </Typography2>
          <ul css={allowlistContainer()} ref={allowlistContainerRef}>
            {allowlist.map((allowedDomain) => (
              <li key={allowedDomain.emailDomain} css={allowlistItem()}>
                <Typography2 component="p" variant="bodyPrimary">
                  {allowedDomain.emailDomain}
                </Typography2>
                <Button
                  variant="ghost"
                  icon={<TrashIcon />}
                  iconPosition="before"
                  size="small"
                  onClick={() => onDeleteDomainClick(allowedDomain.emailDomain)}
                >
                  {_t('Remove')}
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div css={{ display: 'flex' }}>
        {onContinue && (
          <TrackedButton
            {...Button.defaultProps}
            variant="primary"
            icon={<ArrowNextIcon />}
            style={{ marginRight: 'var(--cds-spacing-300)' }}
            css={shouldContinue ? null : disabledButtonStyle}
            onClick={onContinueClick}
            size="medium"
            data-e2e="continue"
            trackingName="invite_learners_allowlist_continue"
          >
            {_t('Continue')}
          </TrackedButton>
        )}
        {onCancel && (
          <TrackedButton
            {...Button.defaultProps}
            variant="secondary"
            onClick={onCancel}
            size="medium"
            data-e2e="cancel"
            trackingName="invite_learners_allowlist_cancel"
          >
            {cancelLabel ?? _t('Cancel')}
          </TrackedButton>
        )}
        {onReturnToAdminHome && (
          <TrackedButton
            {...Button.defaultProps}
            variant="secondary"
            onClick={onReturnToAdminHome}
            size="medium"
            trackingName="invite_learners_allowlist_cancel_to_admin_home"
            css={{ marginLeft: 'auto' }}
          >
            {_t('Cancel')}
          </TrackedButton>
        )}
      </div>
      <InviteLearnersByAllowlistModal
        onClose={onModalClose}
        customError={customError}
        modalState={modalState}
        onDeleteDomainConfirmClick={onDeleteDomainConfirmClick}
      />
    </TrackedDiv>
  );
};

export default compose<Props, PropsFromCaller>(
  graphql<
    PropsFromCaller,
    EnterpriseUserProgramAllowListQueryResults,
    EnterpriseUserProgramAllowListQueryVariables,
    PropsFromEnterpriseUserProgramAllowListQuery
  >(enterpriseUserProgramAllowListQuery, {
    options: ({ programId }) => {
      return {
        variables: {
          programId,
          rosterIds: [],
        },
        context: { clientName: 'gatewayGql' },
      };
    },
    props: ({ data }) => {
      const allowlistData = filterExistsOrDefault(data?.ProgramInvitationsDomainAllowList?.queryDomainAllowList);
      return {
        allowlist: allowlistData,
        refetchAllowlist: data?.refetch,
      };
    },
  }),
  Naptime.createContainer<{}, Props>(() => ({}))
)(InviteLearnersByAllowlistInputs);
